import { useDocMotionGeometry } from "../animations/use-doc-motion-geometry";
import { memo, useEffect, useMemo, type ReactNode } from "react";
import { AbsoluteFill, Img, useCurrentFrame, useVideoConfig } from "remotion";
import { parseDoc } from "@hightouch/lib/ad-studio/html-editor/parse";
import type { Doc, Layer } from "@hightouch/lib/ad-studio/html-editor/model";
import {
  AD_CSS_ISOLATION_CLASS,
  type DocRenderOptions,
  docRootStyle,
  renderLayer,
} from "../doc-renderer";
import {
  layerBounds,
  type MotionGeometry,
} from "../animations/doc-motion-geometry";
import {
  type AnimationBlock,
  parseAnimationBlocks,
} from "@hightouch/lib/ad-studio/html-editor/animation-blocks";
import type { BlockEvalContext } from "../animations/block-style-at";
import {
  blockAnimatedContent,
  blockAnimatedSvg,
  layerBlockStyle,
} from "../animations/layer-block-style";
import { useAssetResolver } from "../asset-resolver-context";
import {
  HIDDEN_PRELOAD_STYLE,
  loadedFontFamilies,
  useFetchedAssetText,
} from "./asset-text";
import {
  collectDocFontFamilies,
  collectDocImageSources,
  resolveDocAssets,
  withoutLayers,
} from "./html-overlay-doc";
import type { HtmlOverlayProps } from "../html-overlay-composition";

/** CSS generic and keyword families that never need a registered font file. */
const GENERIC_FONT_FAMILIES: ReadonlySet<string> = new Set([
  "sans-serif",
  "serif",
  "monospace",
  "cursive",
  "fantasy",
  "system-ui",
  "ui-sans-serif",
  "ui-serif",
  "ui-monospace",
  "ui-rounded",
  "math",
  "emoji",
  "fangsong",
  "inherit",
  "initial",
  "unset",
]);

/**
 * An HTML overlay composited over the clips beneath it.
 *
 * The reason the static ad editor can edit a video's overlay: the markup here
 * is the same markup
 * that editor loads and saves, painted by the same `doc-renderer` core the
 * editor's canvas uses. Editor pixels and rendered frames therefore agree by
 * construction rather than by two implementations being kept in step.
 *
 * Animation lives in the composition's `animations` sidecar, keyed by layer
 * id: a list of `AnimationBlock`s per layer, evaluated by the block engine as
 * CSS computed per frame (never CSS animations, which would not advance with
 * the render clock). The markup's own `data-ht-motion` is the editor's mirror
 * of the same blocks and is not read here, so a stale attribute can never
 * disagree with what the sidecar says. Only v2 sidecars reach here:
 * `CompositionRoot` throws on a composition version this build predates.
 */
export function HtmlOverlayLayer({
  src,
  animations,
  exclude,
}: Omit<HtmlOverlayProps, "transform">) {
  const resolve = useAssetResolver();
  const { fps } = useVideoConfig();
  const htmlText = useFetchedAssetText(src, "Loading HTML overlay");

  const doc = useMemo<Doc | null>(() => {
    if (!htmlText) return null;
    try {
      return parseDoc(htmlText);
    } catch (err) {
      console.error("HtmlOverlay: failed to parse HTML", err);
      return null;
    }
  }, [htmlText]);

  const blocksById = useMemo<ReadonlyMap<string, AnimationBlock[]>>(() => {
    const parsed = new Map<string, AnimationBlock[]>();
    if (!animations) return parsed;
    for (const [layerId, raw] of Object.entries(animations)) {
      const blocks = parseAnimationBlocks(raw);
      if (blocks.length) parsed.set(layerId, blocks);
    }
    return parsed;
  }, [animations]);

  const visible = useMemo<Doc | null>(() => {
    if (!doc) return null;
    return withoutLayers(resolveDocAssets(doc, resolve), exclude);
  }, [doc, resolve, exclude]);

  // Detection metric: warn when the overlay references a font-family that
  // isn't registered as a loaded FontFace. These silently fall back to system
  // fonts (with different defaults in browser preview vs. Lambda's headless
  // Chromium) — the most common cause of preview-vs-export typography drift.
  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    void (async () => {
      const registered = await loadedFontFamilies();
      if (cancelled) return;
      const unregistered = Array.from(collectDocFontFamilies(doc)).filter(
        (family) =>
          !GENERIC_FONT_FAMILIES.has(family.toLowerCase()) &&
          !registered.has(family.toLowerCase()),
      );
      if (unregistered.length > 0) {
        console.warn(
          "[ad-studio] HTML overlay references font-family values that are not registered. " +
            "Will fall back to system fonts (preview and export may diverge).",
          { unregistered, registered: Array.from(registered) },
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doc]);

  // Preload every bitmap the Doc references via Remotion's <Img>, which opens
  // its own delayRender AND populates Chromium's image cache so the plain
  // <img> elements the doc-renderer emits reuse it without a second download.
  const bitmapPreloadUrls = useMemo(() => {
    if (!visible) return [];
    const seen = new Set<string>();
    for (const url of collectDocImageSources(visible)) {
      if (!/^https?:/.test(url)) continue;
      seen.add(url);
    }
    return Array.from(seen);
  }, [visible]);

  // Resolved once per Doc, not once per frame: an animation that slides a
  // layer off-canvas needs the layer's box and the canvas size, which the
  // markup determines and the clock doesn't touch.
  const { geometry, measurement } = useDocMotionGeometry(
    visible ?? undefined,
    AD_CSS_ISOLATION_CLASS,
  );

  // Keep the static Doc element stable across Remotion frames. Each animated
  // layer subscribes to `useCurrentFrame()` inside its own component boundary,
  // so static siblings and the overlay root do no work when only one
  // transform changes.
  const rendered = useMemo(
    () =>
      visible ? (
        <HtmlOverlayBlocksDoc
          doc={visible}
          fps={fps}
          geometry={geometry}
          blocksById={blocksById}
        />
      ) : null,
    [visible, fps, geometry, blocksById],
  );

  if (!visible) return null;

  return (
    <AbsoluteFill>
      {bitmapPreloadUrls.map((url) => (
        <Img
          key={url}
          src={url}
          style={HIDDEN_PRELOAD_STYLE}
          // Hold playback until the bitmap is decodable, so the overlay never
          // paints with a gap where its image belongs.
          pauseWhenLoading
        />
      ))}
      <div className={AD_CSS_ISOLATION_CLASS} style={docRootStyle(visible)}>
        {rendered}
      </div>
      {measurement}
    </AbsoluteFill>
  );
}

interface BlocksDocContext {
  fps: number;
  geometry: MotionGeometry | undefined;
  blocksById: ReadonlyMap<string, AnimationBlock[]>;
}

const HtmlOverlayBlocksDoc = memo(function HtmlOverlayBlocksDoc({
  doc,
  ...context
}: { doc: Doc } & BlocksDocContext) {
  return <>{renderBlockLayerNodes(doc.layers, false, context)}</>;
});

function renderBlockLayerNodes(
  layers: readonly Layer[],
  inFlow: boolean,
  context: BlocksDocContext,
): ReactNode {
  return layers.map((layer) => (
    <BlockLayerNode
      key={layer.id}
      layer={layer}
      inFlow={inFlow}
      context={context}
    />
  ));
}

const BlockLayerNode = memo(function BlockLayerNode({
  layer,
  inFlow,
  context,
}: {
  layer: Layer;
  inFlow: boolean;
  context: BlocksDocContext;
}) {
  return context.blocksById.get(layer.id)?.length ? (
    <BlockAnimatedLayerNode layer={layer} inFlow={inFlow} context={context} />
  ) : (
    renderBlockLayerAt(layer, inFlow, context)
  );
});

function BlockAnimatedLayerNode({
  layer,
  inFlow,
  context,
}: {
  layer: Layer;
  inFlow: boolean;
  context: BlocksDocContext;
}) {
  const frame = useCurrentFrame();
  return renderBlockLayerAt(layer, inFlow, context, frame);
}

function blockEvalContext(
  layer: Layer,
  context: BlocksDocContext,
): BlockEvalContext {
  return {
    fps: context.fps,
    bounds: layerBounds(context.geometry, layer.id),
  };
}

function renderBlockLayerAt(
  layer: Layer,
  inFlow: boolean,
  context: BlocksDocContext,
  frame?: number,
): ReactNode {
  const renderChildren: DocRenderOptions["renderChildren"] = (
    children,
    childrenInFlow,
  ) => renderBlockLayerNodes(children, childrenInFlow, context);

  const blocks = context.blocksById.get(layer.id);
  if (frame === undefined || !blocks?.length) {
    return renderLayer(layer, inFlow, { renderChildren });
  }

  const evalContext = blockEvalContext(layer, context);
  const renderedLayer =
    layer.type === "text"
      ? blockAnimatedContent(layer, frame, blocks, evalContext)
      : layer.type === "svg"
        ? blockAnimatedSvg(layer, blocks)
        : layer;
  return renderLayer(renderedLayer, inFlow, {
    renderChildren,
    layerStyle: (candidate, candidateInFlow) =>
      layerBlockStyle(candidate, candidateInFlow, frame, blocks, evalContext),
  });
}
